import { useEffect, useState } from "react";
import moment, { Moment } from "moment";
import {
  StyledHeader,
  PageContainer,
  ContentContainer,
  PageNavFlex,
  SpinnerContainer,
  StyledTable,
  TableHead,
  TableBody,
  StyledRow,
  CallTypeCell,
  ClientCodeCell,
  UsernameCell,
  DateCell,
  FileNameCell,
  RequestCell,
  ResponseCell,
  TableFooter,
  DownloadText,
  StyledGrid,
  Row,
  InputRow,
} from "./APICallLogStyles";
import { getLogQueryAutofill, getLogs } from "./GetLogHttp";
import {
  APICallLogRecord,
  FormLogState,
  InputSelectedState,
  QueryInputSelected,
} from "./apiCallLogTypes";
import { APICallLogApiResult } from "./apiCallLogTypes";
import {
  CircularProgress,
  DatePickers,
  IconButton,
  TextField,
  Autocomplete,
} from "drift-ui";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const APICallLogs = () => {
  const [formLogState, setFormLogState] = useState<FormLogState>({
    mgUsername: "",
    clientCode: "",
    type: "",
    requestDate: "",
    page: 1,
  });

  const [querySelected, setQuerySelected] = useState<QueryInputSelected>({
    mgUsername: "",
    type: "",
    clientCode: "",
    requestDate: "",
    page: 1,
  });

  const [inputSelectedState, setInputSelectedState] =
    useState<InputSelectedState>({
      mgUsername: [],
      clientCode: [],
      type: [],
      requestDate: "",
    });

  const [isLoading, toggleIsLoading] = useState<boolean>(false);

  const [logData, setLogData] = useState<APICallLogApiResult>();

  const getLogData = async (query: QueryInputSelected) => {
    toggleIsLoading(true);
    try {
      const _getLogData = await getLogs(query);
      setLogData(_getLogData);
    } catch (e) {
      console.log("error :>> ", e);
    }
    toggleIsLoading(false);
  };

  useEffect(() => {
    getLogData(querySelected);
  }, [querySelected]);

  const handleOnChange = async (
    name: string,
    value: string,
    requestDate?: string,
  ) => {
    setFormLogState({ ...formLogState, [name]: value });
    if (value.length > 2) {
      const data: any = await getLogQueryAutofill([name, value]);
      if (data.error) {
        return data.error;
      }
      setInputSelectedState({ ...inputSelectedState, [name]: data });
    }
    if (requestDate) {
      setFormLogState({ ...formLogState, [name]: requestDate });
      setQuerySelected({ ...formLogState, [name]: requestDate });
    }
  };

  const handleOnClick = async (name: string, value: string) => {
    setFormLogState({ ...formLogState, [name]: value });
    setInputSelectedState({ ...inputSelectedState, [name]: [] });
    setQuerySelected({ ...formLogState, [name]: value });
  };

  const handlePageClick = async (pageNumber: number) => {
    setFormLogState({ ...formLogState, page: pageNumber });
    setQuerySelected({ ...formLogState, page: pageNumber });
  };

  const { type, clientCode, mgUsername } = inputSelectedState;

  const handleDownloadClick = async (fileName: string, content: any) => {
    const xmlData = new Blob([content], { type: "text/xml" });
    var element = document.createElement("a");
    element.setAttribute("href", window.URL.createObjectURL(xmlData));
    element.setAttribute("download", `${fileName}.xml`);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const renderTable = () => {
    return logData ? (
      logData.data.map((log) => {
        var date = new Date(log.requestDate);
        const parser = new DOMParser();
        const xmlData = parser.parseFromString(
          log.request as unknown as string,
          "text/xml",
        );
        const tagName = log.type === "ImportWeb" ? "FileName" : "request-id";
        const fileNameElement = xmlData.getElementsByTagName(tagName)[0];
        return (
          <StyledRow>
            <CallTypeCell>{log.type}</CallTypeCell>
            <ClientCodeCell>{log.clientCode}</ClientCodeCell>
            <UsernameCell>{log.mgUsername}</UsernameCell>
            <DateCell>
              {date.toLocaleTimeString([], {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </DateCell>
            <FileNameCell>{fileNameElement?.textContent}</FileNameCell>
            <RequestCell>
              <DownloadText
                onClick={() => {
                  handleDownloadClick(
                    "Request_".concat(log.requestDate),
                    log.request,
                  );
                }}
              >
                Download
              </DownloadText>
            </RequestCell>
            <ResponseCell>
              <DownloadText
                onClick={() => {
                  handleDownloadClick(
                    "Response_".concat(log.requestDate),
                    log.response,
                  );
                }}
              >
                Download
              </DownloadText>
            </ResponseCell>
          </StyledRow>
        );
      })
    ) : (
      <></>
    );
  };

  const renderPageNumber = (logData: {
    data: APICallLogRecord[];
    limit: any;
    page: any;
    totalPages: number;
  }) => {
    return logData.page === logData.totalPages ? (
      <p>
        {(logData.page * logData.limit - logData.limit + 1).toLocaleString()}-
        {(
          logData.page * logData.limit -
          logData.limit +
          logData.data.length
        ).toLocaleString()}
        {" of "}
        {(
          logData.totalPages * logData.limit -
          logData.limit +
          logData.data.length
        ).toLocaleString()}
      </p>
    ) : (
      <p>
        {(logData.page * logData.limit - logData.limit + 1).toLocaleString()}-
        {(logData.page * logData.limit).toLocaleString()} of{" "}
        {(logData.totalPages * logData.limit).toLocaleString()}
      </p>
    );
  };
  return (
    <PageContainer>
      <ContentContainer>
        <Row>
          <StyledHeader>API Call Log</StyledHeader>
        </Row>

        <InputRow>
          <Autocomplete
            sx={{ width: "100%" }}
            onChange={(event, newValue) => {
              handleOnClick("type", newValue);
            }}
            onInputChange={(event, newInputValue) => {
              handleOnChange("type", newInputValue);
            }}
            value={formLogState.type}
            getOptionLabel={(option) => option}
            options={type}
            filterOptions={(x) => x}
            renderInput={(params) => {
              const { InputProps, inputProps, ...restParams } = params;
              return (
                <TextField
                  {...restParams}
                  label="Call type"
                  variant="filled"
                  sx={{ padding: 0 }}
                  slotProps={{
                    input: {
                      ...InputProps,
                      endAdornment: <>{InputProps?.endAdornment}</>,
                    },
                    htmlInput: {
                      ...inputProps,
                    },
                  }}
                />
              );
            }}
          />

          <Autocomplete
            sx={{ width: "100%" }}
            onChange={(event, newValue) => {
              handleOnClick("clientCode", newValue);
            }}
            onInputChange={(event, newInputValue) => {
              handleOnChange("clientCode", newInputValue);
            }}
            value={formLogState.clientCode}
            getOptionLabel={(option) => option}
            options={clientCode}
            filterOptions={(x) => x}
            renderInput={(params) => {
              const { InputProps, inputProps, ...restParams } = params;
              return (
                <TextField
                  {...restParams}
                  label="Client Code"
                  variant="filled"
                  sx={{ padding: 0 }}
                  slotProps={{
                    input: {
                      ...InputProps,
                      endAdornment: <>{InputProps?.endAdornment}</>,
                    },
                    htmlInput: {
                      ...inputProps,
                    },
                  }}
                />
              );
            }}
          />

          <Autocomplete
            sx={{ width: "100%" }}
            onChange={(event, newValue) => {
              handleOnClick("mgUsername", newValue);
            }}
            onInputChange={(event, newInputValue) => {
              handleOnChange("mgUsername", newInputValue);
            }}
            value={formLogState.mgUsername}
            getOptionLabel={(option) => option}
            options={mgUsername}
            filterOptions={(x) => x}
            renderInput={(params) => {
              const { InputProps, inputProps, ...restParams } = params;
              return (
                <TextField
                  {...restParams}
                  label="Username"
                  variant="filled"
                  sx={{ padding: 0 }}
                  slotProps={{
                    input: {
                      ...InputProps,
                      endAdornment: <>{InputProps?.endAdornment}</>,
                    },
                    htmlInput: {
                      ...inputProps,
                    },
                  }}
                />
              );
            }}
          />

          <DatePickers.DatePicker
            label="Date"
            value={
              formLogState.requestDate ? moment(formLogState.requestDate) : null
            }
            maxDate={moment().add(1, "hours")}
            onChange={(date: Moment) => {
              handleOnChange("requestDate", "", date.format("YYYY-MM-DD"));
            }}
            sx={{ width: "100%" }}
            // readOnly
            slotProps={{
              textField: {
                variant: "filled",
                inputProps: {
                  readOnly: true,
                },
              },
            }}
          />
        </InputRow>
        <Row>
          {isLoading ? (
            <SpinnerContainer>
              <CircularProgress />
            </SpinnerContainer>
          ) : (
            <>
              <StyledGrid container size={12}>
                <StyledTable>
                  <TableHead>
                    <StyledRow>
                      <CallTypeCell>CALL TYPE</CallTypeCell>
                      <ClientCodeCell>CLIENT CODE</ClientCodeCell>
                      <UsernameCell>USERNAME</UsernameCell>
                      <DateCell>DATE & TIME</DateCell>
                      <FileNameCell>FILE NAME</FileNameCell>
                      <RequestCell>REQUEST DOWNLOAD</RequestCell>
                      <ResponseCell>RESPONSE DOWNLOAD</ResponseCell>
                    </StyledRow>
                  </TableHead>
                  <TableBody>{renderTable()}</TableBody>
                </StyledTable>
              </StyledGrid>
              <TableFooter>
                {logData ? (
                  <PageNavFlex>
                    {renderPageNumber(logData)}
                    <IconButton
                      onClick={() => {
                        handlePageClick(logData?.page - 1);
                      }}
                    >
                      <ChevronLeft style={{ fontSize: "24px" }} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        handlePageClick(logData?.page + 1);
                      }}
                    >
                      <ChevronRight style={{ fontSize: "24px" }} />
                    </IconButton>
                  </PageNavFlex>
                ) : (
                  <></>
                )}
              </TableFooter>
            </>
          )}
        </Row>
      </ContentContainer>
    </PageContainer>
  );
};

export default APICallLogs;
